import type { AverageWindow } from '@wf/shared'
import { formatDateDE } from '../time/format'
import { useArbzgAverage } from './useZeiten'

/**
 * § 3 ArbZG-Durchschnitt (docs/DESIGN-zeit-arbzg-durchschnitt.md): der Acht-Stunden-Schnitt über das
 * rollierende Ausgleichsfenster.
 *
 * Die Karte legt den RECHENWEG offen — Werktage, neutrale Tage, Summe, Budget. Das ist kein Detailverliebtheit:
 * eine nackte Prozentzahl, die jemanden in die Bußgeldzone stellt (§ 22 ArbZG), kann niemand nachprüfen und
 * niemand entkräften. Wer die Zahl anzweifelt, muss sehen können, woraus sie besteht.
 */

/** Sekunden → „1.152,0 h" mit Dezimal-Komma. */
const h = (sec: number): string => (sec / 3600).toFixed(1).replace('.', ',')
/** Tages-Faktoren mit halben Tagen lesbar machen („23,5"). */
const tage = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ','))

const LEVEL_LABEL = { gruen: 'unauffällig', gelb: 'wird knapp', rot: 'Grenze gerissen' } as const
const VARIANT_LABEL = { '24_wochen': '24 Wochen', '6_monate': 'sechs Kalendermonate' } as const

function WindowRow({ w }: { w: AverageWindow }) {
  const anteil = w.utilization == null ? null : Math.round(w.utilization * 100)
  return (
    <tr>
      <th scope="row">{VARIANT_LABEL[w.variant]}</th>
      <td>
        {formatDateDE(w.from)} – {formatDateDE(w.to)}
      </td>
      <td>
        {tage(w.anrechenbareWerktage)}
        {w.neutraleTage > 0 ? <span className="hint"> (von {tage(w.werktage)})</span> : null}
      </td>
      <td>{h(w.workedSeconds)}</td>
      <td>{h(w.budgetSeconds)}</td>
      <td className={w.headroomSeconds < 0 ? 'wf-neg' : undefined}>
        {w.headroomSeconds < 0 ? '−' : ''}
        {h(Math.abs(w.headroomSeconds))}
      </td>
      <td>{anteil == null ? '—' : `${anteil} %`}</td>
    </tr>
  )
}

export function ArbzgAverageCard({ userId, enabled = true }: { userId?: number; enabled?: boolean }) {
  const avg = useArbzgAverage(userId, enabled)

  if (!enabled) return null
  if (avg.isLoading) return <div className="muted">Acht-Stunden-Durchschnitt wird geladen…</div>
  if (avg.isError || !avg.data) {
    return (
      <div className="error" role="alert">
        Acht-Stunden-Durchschnitt konnte nicht geladen werden.
      </div>
    )
  }
  const { guard, ausgleichszeitraum, federalStateMissing } = avg.data
  if (guard.windows.length === 0) {
    return (
      <div className="wf-card wf-zscard" aria-label="Acht-Stunden-Durchschnitt">
        <div className="wf-zscard__label">Acht-Stunden-Durchschnitt (§ 3 ArbZG)</div>
        {/* Unter 18 gilt das JArbSchG ANSTELLE des ArbZG (§ 18 Abs. 2) — kein Fenster, kein Ergebnis. */}
        <p className="hint">
          Für unter 18-Jährige gilt der Jugendarbeitsschutz statt des Arbeitszeitgesetzes — die Grenzen stehen bei den
          Jugendschutz-Regeln.
        </p>
      </div>
    )
  }
  // Das am stärksten ausgeschöpfte Fenster trägt die Aussage; die Tabelle zeigt trotzdem beide.
  const fuehrend = [...guard.windows].sort((a, b) => (b.utilization ?? 0) - (a.utilization ?? 0))[0]!
  const partial = guard.windows.some((w) => w.partial)
  const unsicher = Math.max(...guard.windows.map((w) => w.unsichereTage))

  return (
    <div className="wf-card wf-zscard" aria-label="Acht-Stunden-Durchschnitt">
      <div className="wf-zscard__label">Acht-Stunden-Durchschnitt (§ 3 ArbZG)</div>

      <div className={guard.level === 'rot' ? 'wf-ztwarn' : guard.level === 'gelb' ? 'wf-ztwarn wf-ztwarn--hint' : 'wf-zsok'} role={guard.level === 'rot' ? 'alert' : 'status'}>
        <span aria-hidden="true">{guard.level === 'rot' ? '⚠' : guard.level === 'gelb' ? '!' : '✓'}</span>
        <span>
          <strong>{LEVEL_LABEL[guard.level]}</strong> —{' '}
          {fuehrend.headroomSeconds >= 0
            ? `noch ${h(fuehrend.headroomSeconds)} h Luft im Ausgleichszeitraum.`
            : `${h(-fuehrend.headroomSeconds)} h über der zulässigen Gesamtarbeitszeit.`}
          {partial ? ' Zwischenstand — der Ausgleichszeitraum läuft noch und lässt sich mit kürzeren Tagen einfangen.' : ''}
        </span>
      </div>

      <table className="wf-table" aria-label="Rechenweg des Acht-Stunden-Durchschnitts">
        <thead>
          <tr>
            <th>Zeitraum</th>
            <th>von – bis</th>
            <th>Werktage</th>
            <th>Arbeitszeit</th>
            <th>zulässig</th>
            <th>Luft</th>
            <th>Ausschöpfung</th>
          </tr>
        </thead>
        <tbody>
          {guard.windows.map((w) => (
            <WindowRow key={w.variant} w={w} />
          ))}
        </tbody>
      </table>

      <p className="hint">
        Zulässig sind acht Stunden je Werktag (Mo–Sa ohne Feiertage) im Durchschnitt — zehn Stunden am Tag nur, wenn
        sie im Ausgleichszeitraum wieder eingefangen werden. Urlaub und Krankheit sind herausgerechnet: sie sind ein
        eigener Anspruch und gleichen keine Mehrarbeit aus. Freizeitausgleich zählt dagegen mit, denn er <em>ist</em>{' '}
        der Ausgleich.
      </p>

      {guard.ambiguous ? (
        <p className="hint">
          Nur eines der beiden gesetzlichen Fenster liegt über der Grenze. Das Gesetz stellt beide zur Wahl — welches
          für euch gilt, hängt vom festgelegten Ausgleichszeitraum ab. In den Einstellungen eintragen, dann rechnet die
          App nur noch damit.
        </p>
      ) : null}

      {ausgleichszeitraum == null && !guard.ambiguous ? (
        <p className="hint">Ausgleichszeitraum nicht festgelegt — es werden beide gesetzlichen Varianten gerechnet.</p>
      ) : null}

      {unsicher > 0 ? (
        <p className="hint">
          {unsicher} {unsicher === 1 ? 'Tag' : 'Tage'} mit unsicheren Zeiten (vermutlich vergessenes Ausstempeln). Diese
          Tage sind aus der Rechnung genommen — die Arbeitszeit oben ist deshalb eine Untergrenze.
        </p>
      ) : null}

      {federalStateMissing ? (
        <p className="hint">
          ⚠ Kein Bundesland in den Einstellungen hinterlegt. Ohne Feiertagskalender zählen Feiertage als Werktage und die
          zulässige Gesamtarbeitszeit fällt <strong>zu hoch</strong> aus — die Warnung käme also zu spät.
        </p>
      ) : null}
    </div>
  )
}
